import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import api from '../services/api';
import { swal } from '../utils/swal';
import './PostulacionPublica.css';

interface DatosPostulado {
  email: string;
  nombre: string;
  tipo_documento: string;
  numero_documento: string;
  lugar_expedicion_documento?: string;
  nombres: string;
  apellidos: string;
  fecha_nacimiento: string;
  telefono: string;
  celular: string;
  direccion: string;
  ciudad: string;
  departamento: string;
  afp?: string;
  eps?: string;
  arl?: string;
  numero_cuenta_bancaria?: string;
  tipo_cuenta_bancaria?: string;
  banco?: string;
  categoria_licencia?: string;
  lugar_expedicion_licencia?: string;
  experiencia_laboral: Array<{
    empresa: string;
    cargo: string;
    fecha_inicio: string;
    fecha_fin: string;
    descripcion: string;
  }>;
  estudios: Array<{
    institucion: string;
    titulo: string;
    fecha_inicio: string;
    fecha_fin: string;
  }>;
  referencias: Array<{
    nombre: string;
    telefono: string;
    relacion: string;
  }>;
  vehiculo?: {
    placa: string;
    numero_interno: string;
    marca: string;
    modelo: string;
    año: string;
    color: string;
    numero_motor: string;
    numero_chasis: string;
    tipo_combustible: string;
    capacidad_tanque: string;
    tipo_vehiculo?: string;
    repotenciado?: boolean;
    configuracion?: string;
    capacidad_carga?: string;
    carroceria?: string;
    peso_vacio?: string;
  };
}

const PostulacionPublica: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [postulacion, setPostulacion] = useState<any>(null);
  const [datos, setDatos] = useState<DatosPostulado>({
    email: '',
    nombre: '',
    tipo_documento: 'CC',
    numero_documento: '',
    nombres: '',
    apellidos: '',
    fecha_nacimiento: '',
    telefono: '',
    celular: '',
    direccion: '',
    ciudad: '',
    departamento: '',
    experiencia_laboral: [],
    estudios: [],
    referencias: []
  });
  const [acuerdoFirmado, setAcuerdoFirmado] = useState(false);
  const [autorizacionDatos, setAutorizacionDatos] = useState(false);
  const [mostrarAcuerdo, setMostrarAcuerdo] = useState(false);
  const [mostrarAutorizacion, setMostrarAutorizacion] = useState(false);
  const [firma, setFirma] = useState('');
  const [firmaManuscrita, setFirmaManuscrita] = useState<string | null>(null);
  const [fotoCara, setFotoCara] = useState<string | null>(null);
  const [fotoCaraFile, setFotoCaraFile] = useState<File | null>(null);
  const [paso, setPaso] = useState(1); // 1: Datos personales, 2: Referencias Laborales, 3: Vehículo, 4: Autorizaciones
  const [incluirVehiculo, setIncluirVehiculo] = useState(false);
  const [datosVehiculo, setDatosVehiculo] = useState({
    placa: '',
    numero_interno: '',
    marca: '',
    modelo: '',
    año: '',
    color: '',
    numero_motor: '',
    numero_chasis: '',
    tipo_combustible: 'Diesel',
    capacidad_tanque: '',
    tipo_vehiculo: '',
    repotenciado: false,
    configuracion: '',
    capacidad_carga: '',
    carroceria: '',
    peso_vacio: ''
  });
  const [documentosVehiculo, setDocumentosVehiculo] = useState({
    soat_numero: '',
    soat_vigencia: '',
    soat_aseguradora: '',
    poliza_resp_civil_numero: '',
    poliza_resp_civil_vigencia: '',
    poliza_resp_civil_aseguradora: '',
    tecnomecanica_numero: '',
    tecnomecanica_vigencia: '',
    tarjeta_propiedad_numero: '',
    tarjeta_propiedad_vigencia: ''
  });
  const signatureRef = useRef<SignatureCanvas>(null);

  const obtenerMensajeError = (err: any, fallback: string) => {
    const apiError = err?.response?.data?.error;

    if (typeof apiError === 'string') {
      return apiError;
    }

    if (apiError?.message && typeof apiError.message === 'string') {
      return apiError.message;
    }

    if (err?.message && typeof err.message === 'string') {
      return err.message;
    }

    return fallback;
  };

  // Helper para obtener datos del conductor de forma segura
  const obtenerNombreCompleto = () => {
    const nombres = (datos.nombres || '').toString().trim();
    const apellidos = (datos.apellidos || '').toString().trim();
    if (nombres && apellidos) {
      return `${nombres} ${apellidos}`;
    }
    return null;
  };

  const obtenerDocumento = () => {
    const tipo = datos.tipo_documento ? String(datos.tipo_documento).trim() : '';
    const numero = datos.numero_documento ? String(datos.numero_documento).trim() : '';
    if (tipo && numero) {
      return `${tipo} No. ${numero}`;
    }
    return null;
  };

  const obtenerLugarExpedicion = () => {
    const lugar = (datos.lugar_expedicion_documento || '').toString().trim();
    const ciudad = (datos.ciudad || '').toString().trim();
    const depto = (datos.departamento || '').toString().trim();
    return lugar || ciudad || depto || null;
  };

  useEffect(() => {
    cargarPostulacion();
  }, [token]);

  const cargarPostulacion = async () => {
    try {
      const response = await api.get(`/postulaciones/token/${token}`);
      setPostulacion(response.data);
      if (response.data.datos_postulado) {
        // Asegurar que tipo_documento tenga un valor válido por defecto
        const datosCargados = {
          ...response.data.datos_postulado,
          tipo_documento: response.data.datos_postulado.tipo_documento || 'CC'
        };
        setDatos(datosCargados);
        
        // Cargar datos del vehículo si existen
        if (datosCargados.vehiculo) {
          setDatosVehiculo(datosCargados.vehiculo);
          setIncluirVehiculo(true);
        }
        
        // Cargar documentos del vehículo si existen
        if (datosCargados.documentos_vehiculo) {
          setDocumentosVehiculo(datosCargados.documentos_vehiculo);
        }
      }
      if (response.data.estado === 'completado') {
        setError('Esta postulación ya fue completada');
      }
    } catch (err: any) {
      setError(obtenerMensajeError(err, 'Error al cargar postulación'));
    } finally {
      setLoading(false);
    }
  };

  const guardarDatos = async () => {
    try {
      await api.put(`/postulaciones/token/${token}`, datos);
      return true;
    } catch (err: any) {
      setError(obtenerMensajeError(err, 'Error al guardar datos'));
      return false;
    }
  };


  const agregarReferencia = () => {
    setDatos({
      ...datos,
      referencias: [
        ...datos.referencias,
        { nombre: '', telefono: '', relacion: '' }
      ]
    });
  };

  const limpiarFirma = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setFirmaManuscrita(null);
    }
  };

  const guardarFirma = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      const dataURL = signatureRef.current.toDataURL();
      setFirmaManuscrita(dataURL);
      setError('');
    } else {
      setError('Debe dibujar su firma antes de guardar');
    }
  };

  const completarPostulacion = async () => {
    if (!autorizacionDatos) {
      setError('Debe aceptar la autorización de manejo de datos personales');
      return;
    }

    if (!acuerdoFirmado) {
      setError('Debe aceptar y firmar el acuerdo de confidencialidad');
      return;
    }

    if (!firma.trim()) {
      setError('Debe ingresar su nombre completo como firma digital');
      return;
    }

    if (!firmaManuscrita) {
      setError('Debe dibujar y guardar su firma manuscrita');
      return;
    }

    if (!fotoCara) {
      setError('Debe agregar una foto de su cara');
      return;
    }

    try {
      // Crear FormData para enviar archivos
      const formData = new FormData();
      
      // Agregar datos como JSON string
      formData.append('datos', JSON.stringify({
        ...datos,
        vehiculo: incluirVehiculo ? datosVehiculo : null,
        documentos_vehiculo: incluirVehiculo ? documentosVehiculo : null
      }));
      
      // Agregar archivos
      if (fotoCaraFile) {
        formData.append('foto_cara', fotoCaraFile);
      }
      
      // Agregar otros campos
      formData.append('acuerdo_firmado', 'true');
      formData.append('version_acuerdo', 'V3-18');
      formData.append('firma_digital', firma);
      formData.append('firma_manuscrita', firmaManuscrita);
      formData.append('autorizacion_datos', autorizacionDatos ? 'true' : 'false');
      
      await api.post(`/postulaciones/token/${token}/completar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      await swal.success('¡Postulación completada exitosamente!');
      navigate('/postulacion-completada');
    } catch (err: any) {
      if (err.response?.status === 413) {
        setError('La foto es demasiado grande. Por favor, use una imagen más pequeña.');
      } else {
        setError(obtenerMensajeError(err, 'Error al completar postulación'));
      }
    }
  };

  const validarDatosPaso = (pasoActual: number): boolean => {
    // Limpiar errores previos
    setError('');
    
    if (pasoActual === 1) {
      // Validar datos personales principales
      if (!datos.nombres?.trim() || !datos.apellidos?.trim()) {
        setError('Debe completar los nombres y apellidos');
        // Scroll al mensaje de error
        setTimeout(() => {
          const errorElement = document.querySelector('.error-message');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return false;
      }
      // Validar tipo y número de documento
      // Asegurarse de que tipo_documento tenga un valor válido (puede ser 'CC', 'CE', 'PASAPORTE')
      const tipoDoc = datos.tipo_documento ? String(datos.tipo_documento).trim() : 'CC';
      const numDoc = datos.numero_documento ? String(datos.numero_documento).trim() : '';
      
      console.log('🔍 Validando documento:', { 
        tipoDoc, 
        numDoc, 
        tipo_documento: datos.tipo_documento, 
        numero_documento: datos.numero_documento,
        tipo_documento_raw: datos.tipo_documento,
        esString: typeof datos.tipo_documento === 'string',
        esValido: tipoDoc === 'CC' || tipoDoc === 'CE' || tipoDoc === 'PASAPORTE'
      });
      
      // Verificar que tipo_documento sea válido (no vacío y sea uno de los valores permitidos)
      const tiposValidos = ['CC', 'CE', 'PASAPORTE'];
      if (!tipoDoc || !tiposValidos.includes(tipoDoc)) {
        setError('Debe seleccionar un tipo de documento válido');
        setTimeout(() => {
          const errorElement = document.querySelector('.error-message');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return false;
      }
      
      if (!numDoc || numDoc.length === 0) {
        setError('Debe completar el número de documento');
        setTimeout(() => {
          const errorElement = document.querySelector('.error-message');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return false;
      }
      if (!datos.celular?.trim()) {
        setError('Debe completar el número de celular');
        setTimeout(() => {
          const errorElement = document.querySelector('.error-message');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return false;
      }
      if (!datos.email?.trim()) {
        setError('Debe completar el email');
        setTimeout(() => {
          const errorElement = document.querySelector('.error-message');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return false;
      }
      if (!datos.direccion?.trim()) {
        setError('Debe completar la dirección');
        setTimeout(() => {
          const errorElement = document.querySelector('.error-message');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return false;
      }
      if (!datos.ciudad?.trim() || !datos.departamento?.trim()) {
        setError('Debe completar la ciudad y departamento');
        setTimeout(() => {
          const errorElement = document.querySelector('.error-message');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return false;
      }
      // Validar que se haya subido la foto
      if (!fotoCara || !fotoCaraFile) {
        setError('Debe agregar una foto de su cara');
        setTimeout(() => {
          const errorElement = document.querySelector('.error-message');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return false;
      }
    }
    if (pasoActual === 2) {
      // Validar que si hay referencias, todas estén completas
      if (datos.referencias && datos.referencias.length > 0) {
        const referenciasIncompletas = datos.referencias.some(ref => 
          (ref.nombre?.trim() || ref.telefono?.trim() || ref.relacion?.trim()) && 
          (!ref.nombre?.trim() || !ref.telefono?.trim() || !ref.relacion?.trim())
        );
        if (referenciasIncompletas) {
          setError('Si agregó una referencia, debe completar todos sus datos (nombre, teléfono y relación)');
          setTimeout(() => {
            const errorElement = document.querySelector('.error-message');
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          return false;
        }
      }
    }
    return true;
  };

  const siguientePaso = async () => {
    if (paso < 4) {
      // Limpiar errores previos
      setError('');
      
      // Validar datos del paso actual antes de avanzar
      if (!validarDatosPaso(paso)) {
        // El error ya fue establecido en validarDatosPaso
        return;
      }
      
      // Intentar guardar datos
      const guardado = await guardarDatos();
      if (!guardado) {
        // Si falla el guardado, el error ya fue establecido en guardarDatos
        return;
      }
      
      // Avanzar al siguiente paso
      setPaso(paso + 1);
      setError(''); // Limpiar errores al avanzar
    }
  };

  const anteriorPaso = () => {
    if (paso > 1) {
      setPaso(paso - 1);
    }
  };

  if (loading) {
    return <div className="loading-container">Cargando...</div>;
  }

  if (error && postulacion?.estado !== 'pendiente') {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="postulacion-publica">
      <div className="postulacion-header">
        <img
          src="/logo_enerpetrol.png"
          alt="Enerpetrol Logo"
          className="postulacion-logo"
          onError={(e) => {
            console.error('Error loading logo:', e);
            // Intentar con extensión en mayúsculas como fallback
            if (e.currentTarget.src !== '/logo_enerpetrol.PNG') {
              e.currentTarget.src = '/logo_enerpetrol.PNG';
            }
          }}
        />
        <h1>Postulación - Enerpetrol</h1>
        <p>Complete su información para postularse como Operador/Vehículo</p>
      </div>

      <div className="postulacion-progress">
        <div className={`progress-step ${paso >= 1 ? 'active' : ''}`}>1. Datos Personales</div>
        <div className={`progress-step ${paso >= 2 ? 'active' : ''}`}>2. Referencias Laborales</div>
        <div className={`progress-step ${paso >= 3 ? 'active' : ''}`}>3. Vehículo</div>
        <div className={`progress-step ${paso >= 4 ? 'active' : ''}`}>4. Autorizaciones</div>
      </div>

      <div className="postulacion-form">
        {/* Paso 1: Datos Personales */}
        {paso === 1 && (
          <div className="form-step">
            <h2>Datos Personales</h2>
            
            {/* Grupo 1: Información de Identificación */}
            <div className="form-section">
              <h3 className="section-title">📋 Información de Identificación</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Tipo de Documento *</label>
                  <select
                    value={datos.tipo_documento || 'CC'}
                    onChange={(e) => setDatos({ ...datos, tipo_documento: e.target.value })}
                    required
                  >
                    <option value="CC">Cédula de Ciudadanía</option>
                    <option value="CE">Cédula de Extranjería</option>
                    <option value="PASAPORTE">Pasaporte</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Número de Documento *</label>
                  <input
                    type="text"
                    value={datos.numero_documento}
                    onChange={(e) => setDatos({ ...datos, numero_documento: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Lugar de Expedición del Documento</label>
                  <input
                    type="text"
                    value={datos.lugar_expedicion_documento || ''}
                    onChange={(e) => setDatos({ ...datos, lugar_expedicion_documento: e.target.value })}
                    placeholder="Ej: Bogotá, Medellín"
                  />
                </div>
              </div>
            </div>

            {/* Grupo 2: Información Personal */}
            <div className="form-section">
              <h3 className="section-title">👤 Información Personal</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Nombres *</label>
                  <input
                    type="text"
                    value={datos.nombres}
                    onChange={(e) => setDatos({ ...datos, nombres: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Apellidos *</label>
                  <input
                    type="text"
                    value={datos.apellidos}
                    onChange={(e) => setDatos({ ...datos, apellidos: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Fecha de Nacimiento *</label>
                  <input
                    type="date"
                    value={datos.fecha_nacimiento}
                    onChange={(e) => setDatos({ ...datos, fecha_nacimiento: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Grupo 3: Información de Contacto */}
            <div className="form-section">
              <h3 className="section-title">📞 Información de Contacto</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Teléfono</label>
                  <input
                    type="tel"
                    value={datos.telefono}
                    onChange={(e) => setDatos({ ...datos, telefono: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Celular *</label>
                  <input
                    type="tel"
                    value={datos.celular}
                    onChange={(e) => setDatos({ ...datos, celular: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={datos.email}
                    onChange={(e) => setDatos({ ...datos, email: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Grupo 4: Información de Ubicación */}
            <div className="form-section">
              <h3 className="section-title">📍 Información de Ubicación</h3>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Dirección *</label>
                  <input
                    type="text"
                    value={datos.direccion}
                    onChange={(e) => setDatos({ ...datos, direccion: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Ciudad *</label>
                  <input
                    type="text"
                    value={datos.ciudad}
                    onChange={(e) => setDatos({ ...datos, ciudad: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Departamento *</label>
                  <input
                    type="text"
                    value={datos.departamento}
                    onChange={(e) => setDatos({ ...datos, departamento: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Grupo 5: Información de Seguridad Social */}
            <div className="form-section">
              <h3 className="section-title">🏥 Información de Seguridad Social</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>AFP</label>
                  <input
                    type="text"
                    value={datos.afp || ''}
                    onChange={(e) => setDatos({ ...datos, afp: e.target.value })}
                    placeholder="Ej: Porvenir, Colfondos"
                  />
                </div>

                <div className="form-group">
                  <label>EPS</label>
                  <input
                    type="text"
                    value={datos.eps || ''}
                    onChange={(e) => setDatos({ ...datos, eps: e.target.value })}
                    placeholder="Ej: Famisanar, Sura"
                  />
                </div>

                <div className="form-group">
                  <label>ARL</label>
                  <input
                    type="text"
                    value={datos.arl || ''}
                    onChange={(e) => setDatos({ ...datos, arl: e.target.value })}
                    placeholder="Ej: Sura, Positiva"
                  />
                </div>
              </div>
            </div>

            {/* Grupo 6: Información Bancaria */}
            <div className="form-section">
              <h3 className="section-title">🏦 Información Bancaria</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Número de Cuenta Bancaria</label>
                  <input
                    type="text"
                    value={datos.numero_cuenta_bancaria || ''}
                    onChange={(e) => setDatos({ ...datos, numero_cuenta_bancaria: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Tipo de Cuenta</label>
                  <select
                    value={datos.tipo_cuenta_bancaria || ''}
                    onChange={(e) => setDatos({ ...datos, tipo_cuenta_bancaria: e.target.value })}
                  >
                    <option value="">Seleccione...</option>
                    <option value="AHORROS">Ahorros</option>
                    <option value="CORRIENTE">Corriente</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Banco</label>
                  <input
                    type="text"
                    value={datos.banco || ''}
                    onChange={(e) => setDatos({ ...datos, banco: e.target.value })}
                    placeholder="Ej: Bancolombia, Davivienda"
                  />
                </div>
              </div>
            </div>

            {/* Grupo 7: Información de Licencia */}
            <div className="form-section">
              <h3 className="section-title">🚗 Información de Licencia de Conducción</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Categoría de Licencia (CAT)</label>
                  <input
                    type="text"
                    value={datos.categoria_licencia || ''}
                    onChange={(e) => setDatos({ ...datos, categoria_licencia: e.target.value })}
                    placeholder="Ej: C1, C2, C3"
                  />
                </div>

                <div className="form-group">
                  <label>Lugar de Expedición de Licencia</label>
                  <input
                    type="text"
                    value={datos.lugar_expedicion_licencia || ''}
                    onChange={(e) => setDatos({ ...datos, lugar_expedicion_licencia: e.target.value })}
                    placeholder="Ej: Bogotá, Medellín"
                  />
                </div>
              </div>
            </div>

            {/* Grupo 8: Foto */}
            <div className="form-section">
              <h3 className="section-title">📷 Foto de Identificación</h3>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Foto de la Cara *</label>
                  <input
                    type="file"
                    accept="image/*"
                    capture="user"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Validar tamaño (máximo 5MB)
                        if (file.size > 5 * 1024 * 1024) {
                          setError('La foto es demasiado grande. Por favor, use una imagen menor a 5MB.');
                          return;
                        }
                        setFotoCaraFile(file);
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setFotoCara(event.target?.result as string);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    required
                  />
                  {fotoCara && (
                    <div className="foto-preview">
                      <img src={fotoCara} alt="Foto de la cara" />
                      <button
                        type="button"
                        onClick={() => {
                          setFotoCara(null);
                          setFotoCaraFile(null);
                        }}
                        className="btn-remove-foto"
                      >
                        ✕ Eliminar foto
                      </button>
                    </div>
                  )}
                  <small>Por favor, tome una foto clara de su rostro (máximo 5MB)</small>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Paso 2: Referencias Laborales */}
        {paso === 2 && (
          <div className="form-step">
            <h2>Referencias Laborales (Opcional)</h2>
            <p style={{ color: '#666', marginBottom: '20px', fontSize: '0.9em' }}>
              Puede agregar referencias laborales si lo desea. Si agrega una referencia, debe completar todos sus datos.
            </p>
            {datos.referencias.map((ref, index) => (
              <div key={index} className="referencia-item">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3>Referencia {index + 1}</h3>
                  <button
                    type="button"
                    onClick={() => {
                      const nueva = datos.referencias.filter((_, i) => i !== index);
                      setDatos({ ...datos, referencias: nueva });
                    }}
                    className="btn-remove"
                    style={{ 
                      background: '#dc3545', 
                      color: 'white', 
                      border: 'none', 
                      padding: '8px 15px', 
                      borderRadius: '6px', 
                      cursor: 'pointer',
                      fontSize: '0.9em'
                    }}
                  >
                    🗑️ Eliminar
                  </button>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Nombre Completo *</label>
                    <input
                      type="text"
                      value={ref.nombre}
                      onChange={(e) => {
                        const nueva = [...datos.referencias];
                        nueva[index].nombre = e.target.value;
                        setDatos({ ...datos, referencias: nueva });
                      }}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Teléfono *</label>
                    <input
                      type="tel"
                      value={ref.telefono}
                      onChange={(e) => {
                        const nueva = [...datos.referencias];
                        nueva[index].telefono = e.target.value;
                        setDatos({ ...datos, referencias: nueva });
                      }}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Relación *</label>
                    <input
                      type="text"
                      value={ref.relacion}
                      onChange={(e) => {
                        const nueva = [...datos.referencias];
                        nueva[index].relacion = e.target.value;
                        setDatos({ ...datos, referencias: nueva });
                      }}
                      placeholder="Ej: Jefe anterior, Colega, etc."
                      required
                    />
                  </div>
                </div>
              </div>
            ))}
            <button type="button" onClick={agregarReferencia} className="btn-add">
              + Agregar Referencia
            </button>
          </div>
        )}

        {/* Paso 3: Datos del Vehículo */}
        {paso === 3 && (
          <div className="form-step">
            <h2>Datos del Vehículo (Opcional)</h2>
            <div className="form-group">
              <label className="vehiculo-checkbox-label">
                <input
                  type="checkbox"
                  checked={incluirVehiculo}
                  onChange={(e) => setIncluirVehiculo(e.target.checked)}
                />
                {' '}Tengo un vehículo para agregar
              </label>
            </div>

            {incluirVehiculo && (
              <div>
                {/* INFORMACIÓN DEL VEHÍCULO */}
                <div style={{ marginTop: '20px', marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '15px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px', fontSize: '1.2em' }}>
                    📋 INFORMACIÓN DEL VEHÍCULO
                  </h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Placa *</label>
                      <input
                        type="text"
                        value={datosVehiculo.placa}
                        onChange={(e) => setDatosVehiculo({ ...datosVehiculo, placa: e.target.value.toUpperCase() })}
                        placeholder="ABC123"
                        required={incluirVehiculo}
                      />
                    </div>

                    <div className="form-group">
                      <label>Número Interno</label>
                      <input
                        type="text"
                        value={datosVehiculo.numero_interno}
                        onChange={(e) => setDatosVehiculo({ ...datosVehiculo, numero_interno: e.target.value })}
                        placeholder="Número interno de la empresa"
                      />
                    </div>

                    <div className="form-group">
                      <label>Marca *</label>
                      <input
                        type="text"
                        value={datosVehiculo.marca}
                        onChange={(e) => setDatosVehiculo({ ...datosVehiculo, marca: e.target.value })}
                        placeholder="Ej: Volvo, Mercedes, MACK, etc."
                        required={incluirVehiculo}
                      />
                    </div>

                    <div className="form-group">
                      <label>Línea/Modelo *</label>
                      <input
                        type="text"
                        value={datosVehiculo.modelo}
                        onChange={(e) => setDatosVehiculo({ ...datosVehiculo, modelo: e.target.value })}
                        placeholder="Ej: CXU 613, FH16, etc."
                        required={incluirVehiculo}
                      />
                    </div>

                    <div className="form-group">
                      <label>Color</label>
                      <input
                        type="text"
                        value={datosVehiculo.color}
                        onChange={(e) => setDatosVehiculo({ ...datosVehiculo, color: e.target.value })}
                        placeholder="Ej: BLANCO, NEGRO, etc."
                      />
                    </div>

                    <div className="form-group">
                      <label>Tipo de Vehículo</label>
                      <select
                        value={datosVehiculo.tipo_vehiculo}
                        onChange={(e) => setDatosVehiculo({ ...datosVehiculo, tipo_vehiculo: e.target.value })}
                      >
                        <option value="">Seleccione...</option>
                        <option value="TRACTOCAMION">Tractocamión</option>
                        <option value="CAMION">Camión</option>
                        <option value="CAMIONETA">Camioneta</option>
                        <option value="OTRO">Otro</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Modelo (Año) *</label>
                      <input
                        type="number"
                        value={datosVehiculo.año}
                        onChange={(e) => setDatosVehiculo({ ...datosVehiculo, año: e.target.value })}
                        min="1900"
                        max={new Date().getFullYear() + 1}
                        placeholder="Ej: 2014"
                        required={incluirVehiculo}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={datosVehiculo.repotenciado}
                          onChange={(e) => setDatosVehiculo({ ...datosVehiculo, repotenciado: e.target.checked })}
                        />
                        {' '}Repotenciado
                      </label>
                    </div>

                    <div className="form-group">
                      <label>Configuración</label>
                      <input
                        type="text"
                        value={datosVehiculo.configuracion}
                        onChange={(e) => setDatosVehiculo({ ...datosVehiculo, configuracion: e.target.value })}
                        placeholder="Ej: 383, 4x2, 6x4"
                      />
                    </div>

                    <div className="form-group">
                      <label>Capacidad de Carga (kg)</label>
                      <input
                        type="number"
                        value={datosVehiculo.capacidad_carga}
                        onChange={(e) => setDatosVehiculo({ ...datosVehiculo, capacidad_carga: e.target.value })}
                        min="0"
                        step="0.01"
                        placeholder="Ej: 34000"
                      />
                    </div>

                    <div className="form-group">
                      <label>Carrocería</label>
                      <input
                        type="text"
                        value={datosVehiculo.carroceria}
                        onChange={(e) => setDatosVehiculo({ ...datosVehiculo, carroceria: e.target.value })}
                        placeholder="Ej: S.R.S, Plataforma"
                      />
                    </div>

                    <div className="form-group">
                      <label>Peso Vacío (kg)</label>
                      <input
                        type="number"
                        value={datosVehiculo.peso_vacio}
                        onChange={(e) => setDatosVehiculo({ ...datosVehiculo, peso_vacio: e.target.value })}
                        min="0"
                        step="0.01"
                        placeholder="Ej: 7500"
                      />
                    </div>

                    <div className="form-group">
                      <label>Número de Motor</label>
                      <input
                        type="text"
                        value={datosVehiculo.numero_motor}
                        onChange={(e) => setDatosVehiculo({ ...datosVehiculo, numero_motor: e.target.value })}
                        placeholder="Ej: MP8-1040394-A3-H"
                      />
                    </div>

                    <div className="form-group">
                      <label>Número de Chasis</label>
                      <input
                        type="text"
                        value={datosVehiculo.numero_chasis}
                        onChange={(e) => setDatosVehiculo({ ...datosVehiculo, numero_chasis: e.target.value })}
                        placeholder="Ej: 1M1AW09Y9EM042288"
                      />
                    </div>

                    <div className="form-group">
                      <label>Tipo de Combustible *</label>
                      <select
                        value={datosVehiculo.tipo_combustible}
                        onChange={(e) => setDatosVehiculo({ ...datosVehiculo, tipo_combustible: e.target.value })}
                        required={incluirVehiculo}
                      >
                        <option value="Diesel">Diesel</option>
                        <option value="Gasolina">Gasolina</option>
                        <option value="Gas">Gas</option>
                        <option value="Electrico">Eléctrico</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Capacidad del Tanque (Litros)</label>
                      <input
                        type="number"
                        value={datosVehiculo.capacidad_tanque}
                        onChange={(e) => setDatosVehiculo({ ...datosVehiculo, capacidad_tanque: e.target.value })}
                        min="0"
                        step="0.01"
                        placeholder="Ej: 200"
                      />
                    </div>
                  </div>
                </div>

                {/* DOCUMENTOS Y PÓLIZAS DEL VEHÍCULO */}
                <div style={{ marginTop: '30px', marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '15px', color: '#333', borderBottom: '2px solid #28a745', paddingBottom: '10px', fontSize: '1.2em' }}>
                    📄 DOCUMENTOS Y PÓLIZAS DEL VEHÍCULO
                  </h3>
                  <div className="form-grid">
                    {/* SOAT */}
                    <div className="form-group">
                      <label>SOAT - Número</label>
                      <input
                        type="text"
                        value={documentosVehiculo.soat_numero}
                        onChange={(e) => setDocumentosVehiculo({ ...documentosVehiculo, soat_numero: e.target.value })}
                        placeholder="Ej: 25080043613810"
                      />
                    </div>
                    <div className="form-group">
                      <label>SOAT - Vigencia</label>
                      <input
                        type="date"
                        value={documentosVehiculo.soat_vigencia}
                        onChange={(e) => setDocumentosVehiculo({ ...documentosVehiculo, soat_vigencia: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>SOAT - Aseguradora</label>
                      <input
                        type="text"
                        value={documentosVehiculo.soat_aseguradora}
                        onChange={(e) => setDocumentosVehiculo({ ...documentosVehiculo, soat_aseguradora: e.target.value })}
                        placeholder="Ej: 860002400"
                      />
                    </div>

                    {/* Póliza de Responsabilidad Civil */}
                    <div className="form-group">
                      <label>Póliza Resp. Civil - Número</label>
                      <input
                        type="text"
                        value={documentosVehiculo.poliza_resp_civil_numero}
                        onChange={(e) => setDocumentosVehiculo({ ...documentosVehiculo, poliza_resp_civil_numero: e.target.value })}
                        placeholder="Ej: BCH20003126"
                      />
                    </div>
                    <div className="form-group">
                      <label>Póliza Resp. Civil - Vigencia</label>
                      <input
                        type="date"
                        value={documentosVehiculo.poliza_resp_civil_vigencia}
                        onChange={(e) => setDocumentosVehiculo({ ...documentosVehiculo, poliza_resp_civil_vigencia: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Póliza Resp. Civil - Aseguradora</label>
                      <input
                        type="text"
                        value={documentosVehiculo.poliza_resp_civil_aseguradora}
                        onChange={(e) => setDocumentosVehiculo({ ...documentosVehiculo, poliza_resp_civil_aseguradora: e.target.value })}
                        placeholder="Ej: LA PREVISORA S.A."
                      />
                    </div>

                    {/* Revisión Técnico-Mecánica */}
                    <div className="form-group">
                      <label>Revisión Técnico-Mecánica - Número</label>
                      <input
                        type="text"
                        value={documentosVehiculo.tecnomecanica_numero}
                        onChange={(e) => setDocumentosVehiculo({ ...documentosVehiculo, tecnomecanica_numero: e.target.value })}
                        placeholder="Ej: 179327556"
                      />
                    </div>
                    <div className="form-group">
                      <label>Revisión Técnico-Mecánica - Vigencia</label>
                      <input
                        type="date"
                        value={documentosVehiculo.tecnomecanica_vigencia}
                        onChange={(e) => setDocumentosVehiculo({ ...documentosVehiculo, tecnomecanica_vigencia: e.target.value })}
                      />
                    </div>

                    {/* Tarjeta de Propiedad */}
                    <div className="form-group">
                      <label>Tarjeta de Propiedad - Número</label>
                      <input
                        type="text"
                        value={documentosVehiculo.tarjeta_propiedad_numero}
                        onChange={(e) => setDocumentosVehiculo({ ...documentosVehiculo, tarjeta_propiedad_numero: e.target.value })}
                        placeholder="Número de tarjeta de propiedad"
                      />
                    </div>
                    <div className="form-group">
                      <label>Tarjeta de Propiedad - Vigencia</label>
                      <input
                        type="date"
                        value={documentosVehiculo.tarjeta_propiedad_vigencia}
                        onChange={(e) => setDocumentosVehiculo({ ...documentosVehiculo, tarjeta_propiedad_vigencia: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Paso 4: Autorizaciones y Acuerdos */}
        {paso === 4 && (
          <div className="form-step">
            <h2>Autorizaciones y Acuerdos</h2>
            
            {/* Sección de Autorización de Datos Personales */}
            <div className="acuerdo-section">
              <h3 style={{color: '#0f4222', marginBottom: '15px', fontSize: '1.2em'}}>
                📋 Autorización de Manejo de Datos Personales
              </h3>
              <button
                type="button"
                onClick={() => setMostrarAutorizacion(!mostrarAutorizacion)}
                className="btn-acuerdo"
              >
                {mostrarAutorizacion ? 'Ocultar' : 'Ver'} Texto de Autorización
              </button>

              {mostrarAutorizacion && (
                <div className="acuerdo-content">
                  <div className="acuerdo-texto">
                    <p>
                      <strong>Yo</strong> {(() => {
                        const nombre = obtenerNombreCompleto();
                        console.log('Datos en autorización:', { datos, nombre, documento: obtenerDocumento(), lugar: obtenerLugarExpedicion() });
                        return nombre || '_____________________';
                      })()} identificado con {obtenerDocumento() || '_____________________'} expedido en {obtenerLugarExpedicion() || '_____________________'} autorizo a la
                      empresa ENERPETROL S.A identificada con Nit.800.121.921-1 para que verifique y reporte a
                      cualquier base de datos la documentación e información presentada en el formato "Hoja de
                      vida Vehículo/Operador", en la cual será validada la información como referencias
                      laborales, personales y autenticidad de todos los documentos presentados, con el fin de
                      vincularme en calidad de contratista; Así mismo autorizo que periódicamente se realice el
                      estudio de seguridad donde se validen antecedentes judiciales, Lista Clinton, Procuraduría,
                      Contraloría, Policía Nacional, Consulta de infracciones de tránsito, veracidad de la licencia de
                      conducción, toma de pruebas de alcoholimetrías, sustancias psicoactivas y/o polígrafo cuando
                      se estime conveniente. Esto se realizará directamente o por un tercero autorizado.
                    </p>
                    <p style={{marginTop: '15px'}}>
                      Esta autorización se realiza en cumplimiento de la <strong>Ley 1581 de 2012</strong> y el 
                      <strong> Decreto Reglamentario 1377 de 2013</strong>, sobre protección de datos personales.
                    </p>
                    <p style={{marginTop: '15px', fontSize: '0.9em', color: '#666', fontStyle: 'italic'}}>
                      Para más información visite: <a href="http://www.enerpetrolsa.com" target="_blank" rel="noopener noreferrer">www.enerpetrolsa.com</a>
                    </p>
                  </div>
                </div>
              )}

              <div className="checkbox-container">
                <label className="checkbox-label-required">
                  <input
                    type="checkbox"
                    checked={autorizacionDatos}
                    onChange={(e) => setAutorizacionDatos(e.target.checked)}
                    required
                    className="checkbox-large"
                  />
                  <span className="checkbox-text">
                    <strong>✓ Autorizo el manejo de mis datos personales</strong> según lo establecido en la Ley 1581 de 2012 y Decreto 1377 de 2013 *
                  </span>
                </label>
              </div>
            </div>

            {/* Sección de Acuerdo de Confidencialidad */}
            <div className="acuerdo-section" style={{marginTop: '40px'}}>
              <h3 style={{color: '#0f4222', marginBottom: '15px', fontSize: '1.2em'}}>
                🔒 Acuerdo de Confidencialidad
              </h3>
              <button
                type="button"
                onClick={() => setMostrarAcuerdo(!mostrarAcuerdo)}
                className="btn-acuerdo"
              >
                {mostrarAcuerdo ? 'Ocultar' : 'Ver'} Acuerdo de Confidencialidad
              </button>

              {mostrarAcuerdo && (
                <div className="acuerdo-content">
                  <div className="acuerdo-header">
                    <h3>ACUERDO DE CONFIDENCIALIDAD, SEGURIDAD Y MANEJO DEL OPERADOR DE VEHÍCULO DE CARGA</h3>
                    <p><strong>Versión:</strong> 3</p>
                    <p><strong>Fecha:</strong> 15/05/2018</p>
                  </div>
                  
                  <div className="acuerdo-texto">
                    <p>
                      <strong>Yo,</strong>
                    </p>
                    <p style={{marginLeft: '20px', marginTop: '10px'}}>
                      {obtenerNombreCompleto() || '____________________________________'},
                    </p>
                    <p style={{marginLeft: '20px'}}>
                      identificado con
                    </p>
                    <p style={{marginLeft: '20px'}}>
                      {obtenerDocumento() ? `${datos.tipo_documento?.toLowerCase() || 'cédula'} número ${datos.numero_documento}` : 'cédula número_______________'} de {obtenerLugarExpedicion() || '________________'} manifiesto mi compromiso de apoyar a la
                      transportadora ENERPETROL S.A. en todo lo concerniente a la seguridad de la operación de las
                      mercancías movilizadas por el territorio nacional, por ello me comprometo con la organización
                      en lo siguiente:
                    </p>

                    <ol className="acuerdo-lista">
                      <li>
                        A no revelar a persona alguna la información que él CONOZCA sobre los temas, pactándose
                        que en caso de establecer violación a este acuerdo se configurará justa causa, para la
                        terminación de su contrato, siendo exigibles a favor de la organización los perjuicios que tal
                        conducta le haya causado.
                      </li>
                      <li>
                        Mantener la integridad de la carga entregada: es decir a entregar tal como se recibió en
                        cantidad, tamaño y forma de la misma evitando manipulación mal intencionada.
                      </li>
                      <li>
                        A mantener la ruta establecida para el movimiento de la mercancía y en caso de presentar
                        un obstáculo o evento que requiera un cambio de vía, reportar al Coordinador de seguridad
                        o controlador de tráfico de la Organización a fin de recibir aprobación del cambio.
                      </li>
                      <li>
                        A reportar cualquier novedad que se presente en la unidad de transporte y en la
                        mercancía, para lo cual buscaré una autoridad competente y le informare de la novedad,
                        en todo momento estaré atento a las instrucciones dadas por ENERPETROL S.A.
                      </li>
                      <li>
                        A permitir la inspección del vehículo y del Remolque bajo las listas de verificación y decir la
                        verdad con respecto a las condiciones físicas, de seguridad y mecánicas del vehículo.
                      </li>
                      <li>
                        A velar por la seguridad del vehículo en los sitios de estacionamiento verificando los
                        precintos de seguridad y a hacer rondas de inspección alrededor del vehículo a fin de
                        garantizar integridad de la mercancía, las veces que estime conveniente.
                      </li>
                      <li>
                        A participar activamente en las capacitaciones o prácticas de entrenamiento ofrecidas
                        libremente por Enerpetrol S.A. y a cumplir las establecidas por la normatividad legal
                        vigente.
                      </li>
                      <li>
                        A notificar tan pronto como una autoridad competente haga una inspección al vehículo,
                        para lo cual solicitare aprobación del Coordinador de seguridad de la Organización y seré
                        cuidadoso de dejar anotaciones en los documentos respectivos (guía única de transporte o
                        manifiesto único de carga), como: retiro de precintos.
                      </li>
                      <li>
                        Autorizar a la Organización a que permanentemente verifique la información de honestidad
                        y transparencia de mi conducta y acepto que en caso que la Organización lo defina no
                        laborar más con ella.
                      </li>
                      <li>
                        A no llevar otras cargas que no sean de la Organización sin previa autorización de la misma
                        y cumplir con los horarios y fechas de entrega de la mercancía.
                      </li>
                      <li>
                        A informar oportunamente de manera voluntaria si recibo información de conspiración
                        respecto a posibles hurtos, daños o lesiones a la Organización o a funcionarios de esta.
                      </li>
                      <li>
                        A no consumir o a estar bajos los efectos de bebidas alcohólicas, tabaco, sustancias
                        sicotrópicas o psicoactivas que afecten mi desempeño en la actividad que realizo, así
                        mismo autorizo a la Organización para que cuando lo estime conveniente me practique las
                        correspondientes pruebas.
                      </li>
                      <li>
                        A informar oportunamente sobre mí estado de salud cuando este afecte mí desempeño
                        laboral, en caso de estar bajo tratamiento médico a presentar los documentos
                        correspondientes.
                      </li>
                      <li>
                        A no llevar pasajeros.
                      </li>
                      <li>
                        A cargar el vehículo con la cantidad establecida, para evitar sobrepeso, esto debido a que
                        la Organización no se hace responsable de dicho exceso ni a las sanciones a que haya
                        lugar.
                      </li>
                      <li>
                        A no liderar o ejecutar actividades ilícitas, narcotráfico, terrorismo o cualquier otra
                        actividad que atente contra la integridad, transparencia y debido proceso de Enerpetrol
                        S.A.
                      </li>
                      <li>
                        Me comprometo a cumplir con las funciones y responsabilidades en HSEQ establecidas para
                        el cargo de Operador de Vehículo.
                      </li>
                      <li>
                        Me comprometo a seguir cada una de las políticas, procedimientos, marco legal y demás
                        disposiciones establecidas por Enerpetrol S.A., para el Sistema de Gestión Integral HSEQ,
                        las cuales están consagradas en la cartilla de Inducción que me fue entregada.
                      </li>
                    </ol>

                    <p style={{marginTop: '20px'}}>
                      <strong>Yo</strong>_____________________ identificado como aparece al lado de mi firma autorizó a la
                      empresa ENERPETROL S.A identificada con Nit.800.121.921-1 para que verifique y reporte a
                      cualquier base de datos la documentación e información presentada en el formato "Hoja de
                      vida Vehículo/Operador", en la cual será validada la información como referencias
                      laborales, personales y autenticidad de todos los documentos presentados, con el fin de
                      vincularme en calidad de contratista; Así mismo autorizo que periódicamente se realice el
                      estudio de seguridad donde se validen antecedentes judiciales, Lista Clinton, Procuraduría,
                      Contraloría, Policía Nacional, Consulta de infracciones de tránsito, veracidad de la licencia de
                      conducción, toma de pruebas de alcoholimetrías, sustancias psicoactivas y/o polígrafo cuando
                      se estime conveniente. Esto se realizará directamente o por un tercero autorizado.
                    </p>

                    <p style={{marginTop: '20px'}}>
                      <strong>Fecha de firma del acuerdo:</strong> _____________________
                    </p>

                    <p style={{marginTop: '30px', textAlign: 'right'}}>
                      Cordialmente,
                    </p>
                    <p style={{marginTop: '50px', textAlign: 'right'}}>
                      ____________________________
                    </p>
                    <p style={{marginTop: '5px', textAlign: 'right'}}>
                      Firma
                    </p>
                    <p style={{marginTop: '5px', textAlign: 'right'}}>
                      CC de
                    </p>

                    <p style={{marginTop: '30px', fontSize: '0.9em', color: '#666', fontStyle: 'italic'}}>
                      Este documento es propiedad privada de Enerpetrol S.A., las versiones vigentes y aprobadas se encuentran en la web
                      de la organización
                    </p>
                  </div>
                </div>
              )}

              <div className="checkbox-container">
                <label className="checkbox-label-required">
                  <input
                    type="checkbox"
                    checked={acuerdoFirmado}
                    onChange={(e) => setAcuerdoFirmado(e.target.checked)}
                    required
                    className="checkbox-large"
                  />
                  <span className="checkbox-text">
                    <strong>✓ Acepto y firmo el acuerdo de confidencialidad</strong> y me comprometo a cumplir con todas las condiciones establecidas *
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label>Firma Digital (Ingrese su nombre completo) *</label>
                <input
                  type="text"
                  value={firma}
                  onChange={(e) => setFirma(e.target.value)}
                  placeholder="Ej: Juan Pérez García"
                  required
                />
              </div>

              <div className="form-group">
                <label>Firma Manuscrita *</label>
                <p className="firma-instructions">
                  Dibuje su firma en el campo a continuación usando el mouse o el dedo (en dispositivos táctiles)
                </p>
                <div className="signature-container">
                  <div className="signature-wrapper">
                    <SignatureCanvas
                      ref={signatureRef}
                      canvasProps={{
                        className: 'signature-canvas',
                        width: 600,
                        height: 200
                      }}
                      backgroundColor="#ffffff"
                      penColor="#000000"
                    />
                  </div>
                  <div className="signature-actions">
                    <button type="button" onClick={limpiarFirma} className="btn-clear-signature">
                      🗑️ Limpiar Firma
                    </button>
                    <button type="button" onClick={guardarFirma} className="btn-save-signature">
                      💾 Guardar Firma
                    </button>
                  </div>
                  {firmaManuscrita && (
                    <div className="firma-preview">
                      <p className="firma-success">✓ Firma guardada correctamente</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="form-actions">
          {paso > 1 && (
            <button type="button" onClick={anteriorPaso} className="btn-secondary">
              ← Anterior
            </button>
          )}
          {paso < 4 ? (
            <button type="button" onClick={siguientePaso} className="btn-primary">
              Siguiente →
            </button>
          ) : (
            <button type="button" onClick={completarPostulacion} className="btn-primary">
              ✓ Finalizar Postulación
            </button>
          )}
        </div>

        {error && <div className="error-message" style={{ marginTop: '20px', marginBottom: '10px' }}>{error}</div>}
      </div>
    </div>
  );
};

export default PostulacionPublica;
